/**
 * Estilos da área do colaborador de Solicitações (Suprimentos).
 *
 * MOBILE FIRST: base pensada para ~380px (touch targets >= 48px, fontes de
 * input >= 16px para não disparar zoom no iOS); desktop é adaptação via
 * media queries min-width. Tokens de cor sempre via CSS vars do tema
 * (mesma convenção de Romaneios/styles.jsx — suportam dark mode).
 */
import styled, { keyframes } from "styled-components";

import { BP_MOBILE } from "../../../styles/canonical/breakpoints";
import { getToneTokens } from "../../../styles/shared";

// Acima do breakpoint mobile canônico (768px) a página vira "adaptação
// desktop" — o mesmo corte usado pelo PageContainer (padding 16px ↔ 24px).
const BP_DESKTOP = BP_MOBILE + 1;

// ---------- genéricos ----------

export const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 9px;
  border-radius: 999px;
  white-space: nowrap;
  ${({ $tone }) => {
    const t = getToneTokens(null, $tone || "neutral");
    return `background:${t.background}; color:${t.text}; border:1px solid ${t.border};`;
  }}
`;

export const ErroBox = styled.div`
  padding: 12px 16px;
  border-radius: 10px;
  background: var(--danger-bg);
  color: var(--danger-text);
  border: 1px solid var(--danger-border);
  font-size: 14px;
`;

export const VazioBox = styled.div`
  padding: 36px 20px;
  text-align: center;
  color: var(--ink-2);
  font-size: 14px;
  border: 1px dashed var(--border);
  border-radius: 12px;
`;

// ---------- Hub ----------

export const ListaCartoes = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const TipoCard = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  min-height: 72px;
  padding: 14px 16px;
  text-align: left;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  cursor: pointer;
  color: var(--ink);
  &:active { background: var(--accent-soft); }
  @media (min-width: ${BP_DESKTOP}px) {
    &:hover { border-color: var(--accent); background: var(--accent-soft); }
  }
`;

export const TipoSigla = styled.span`
  flex: none;
  width: 48px;
  height: 48px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  background: var(--accent-soft);
  color: var(--accent);
  font-weight: 700;
  font-size: 14px;
`;

export const TipoTextos = styled.span`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  flex: 1;
`;

export const TipoLabel = styled.span`
  font-size: 15px;
  font-weight: 600;
`;

export const TipoHint = styled.span`
  font-size: 12.5px;
  color: var(--ink-2);
`;

export const SolCard = styled.button`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  padding: 13px 15px;
  text-align: left;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  cursor: pointer;
  color: var(--ink);
  &:active { background: var(--accent-soft); }
`;

export const SolCardTopo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
`;

export const SolNumero = styled.span`
  font-weight: 700;
  font-size: 14.5px;
  font-variant-numeric: tabular-nums;
`;

export const SolMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 10px;
  font-size: 12.5px;
  color: var(--ink-2);
`;

// ---------- Formulário ----------

export const Card = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
  min-width: 0;
`;

export const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-2);
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const Input = styled.input`
  height: 48px;
  width: 100%;
  padding: 8px 14px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  color: var(--ink);
  font-size: 16px;
  box-sizing: border-box;
  &::placeholder { color: var(--ink-3); }
  &:focus { outline: none; border-color: var(--accent); }
`;

export const Select = styled.select`
  height: 48px;
  width: 100%;
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  color: var(--ink);
  font-size: 16px;
  cursor: pointer;
  &:focus { outline: none; border-color: var(--accent); }
`;

export const InlineError = styled.div`
  font-size: 12.5px;
  color: var(--danger-text);
`;

export const Hint = styled.div`
  font-size: 12px;
  color: var(--ink-2);
`;

// Espessura NÃO é digitável: vem do material selecionado (cada espessura
// é um cadastro próprio nas Cores MDF). Display com cara de campo travado.
export const EspessuraDisplay = styled.div`
  display: flex;
  align-items: center;
  min-height: 48px;
  padding: 0 14px;
  border: 1px dashed var(--border);
  border-radius: 10px;
  background: var(--surface-2, var(--surface));
  color: var(--ink);
  font-size: 16px;
`;

export const Linha2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

export const BadgeVeio = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.03em;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--info-bg);
  color: var(--info-text);
  border: 1px solid var(--info-border);
  white-space: nowrap;
`;

// Segmento de 2+ botões grandes (natureza, Sim/Não)
export const SegmentoBotoes = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $cols = 2 }) => $cols}, 1fr);
  gap: 8px;
`;

export const SegmentoBtn = styled.button`
  min-height: 48px;
  padding: 8px 10px;
  border-radius: 10px;
  font-size: 14.5px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${({ $ativo }) => ($ativo ? "var(--accent)" : "var(--border)")};
  background: ${({ $ativo }) => ($ativo ? "var(--accent-soft)" : "var(--surface)")};
  color: ${({ $ativo }) => ($ativo ? "var(--accent)" : "var(--ink-2)")};
`;

export const ToggleLinha = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  > span {
    font-size: 14px;
    font-weight: 600;
    color: var(--ink-2);
  }
  > div {
    display: flex;
    gap: 6px;
    flex: none;
  }
`;

export const ToggleBtn = styled.button`
  min-width: 64px;
  min-height: 44px;
  padding: 0 14px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${({ $ativo }) => ($ativo ? "var(--accent)" : "var(--border)")};
  background: ${({ $ativo }) => ($ativo ? "var(--accent-soft)" : "var(--surface)")};
  color: ${({ $ativo }) => ($ativo ? "var(--accent)" : "var(--ink-2)")};
`;

// Stepper de quantidade (− n +)
export const StepperBox = styled.div`
  display: flex;
  align-items: stretch;
  gap: 8px;
`;

export const StepperBtn = styled.button`
  width: 48px;
  height: 48px;
  flex: none;
  display: grid;
  place-items: center;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  color: var(--ink);
  cursor: pointer;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

export const StepperValor = styled.input`
  width: 72px;
  height: 48px;
  text-align: center;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  color: var(--ink);
  font-size: 16px;
  font-weight: 600;
  &:focus { outline: none; border-color: var(--accent); }
`;

// ---------- Blocos de peça / item ----------

export const PecaCard = styled.div`
  border: 1px solid ${({ $erro }) => ($erro ? "var(--danger-border)" : "var(--border)")};
  border-radius: 12px;
  background: var(--surface);
  overflow: hidden;
`;

export const PecaHead = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 56px;
  padding: 12px 14px;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  color: var(--ink);
`;

export const PecaTitulo = styled.span`
  font-size: 15px;
  font-weight: 700;
  flex: none;
`;

export const PecaResumoTxt = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 12.5px;
  color: var(--ink-2);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const PecaBody = styled.div`
  border-top: 1px solid var(--border);
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const PecaAcoes = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

export const AcaoLeveBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: ${({ $danger }) => ($danger ? "var(--danger-text)" : "var(--ink-2)")};
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  &:active { background: var(--accent-soft); }
`;

// Botão de destaque no detalhe: reabrir o comprovante da solicitação a qualquer
// momento (o usuário costumava perder o JPG por não salvar na tela de envio).
export const VerComprovanteBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 52px;
  border-radius: 12px;
  background: var(--accent);
  color: #fff;
  border: none;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  &:hover { filter: brightness(1.05); }
  &:active { filter: brightness(0.95); }
`;

export const AddBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 52px;
  border-radius: 12px;
  background: transparent;
  color: var(--accent);
  border: 1px dashed var(--accent);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  &:active { background: var(--accent-soft); }
`;

// ---------- Fita de borda ----------

export const FitaWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

export const FitaPlaceholder = styled.div`
  width: 100%;
  min-height: 110px;
  display: grid;
  place-items: center;
  padding: 16px;
  border: 1.5px dashed var(--border);
  border-radius: 12px;
  color: var(--ink-2);
  font-size: 13px;
  text-align: center;
`;

export const ChipsLinha = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
`;

export const ChipBtn = styled.button`
  min-height: 44px;
  padding: 0 16px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink-2);
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  &:active { background: var(--accent-soft); }
`;

export const FitaContador = styled.div`
  font-size: 12.5px;
  color: var(--ink-2);
  font-weight: 600;
`;

// ---------- Fotos ----------

export const FotosWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const FotoAddBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 48px;
  padding: 0 16px;
  border-radius: 10px;
  border: 1px dashed var(--accent);
  background: transparent;
  color: var(--accent);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

export const FotoThumbBox = styled.div`
  position: relative;
  width: 72px;
  height: 72px;
  flex: none;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid var(--border);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

export const FotoRemoveBtn = styled.button`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  border: none;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.62);
  color: #fff;
  cursor: pointer;
`;

// Aviso quando a câmera não pôde abrir: nunca deixa o usuário sem saída —
// explica o motivo e oferece a galeria, que não depende de permissão.
export const AvisoCamera = styled.div`
  margin-top: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid var(--warning-border, #e6c98a);
  background: var(--warning-bg, #fff6e5);
  color: var(--warning-ink, #6b4e12);
  font-size: 13px;
  line-height: 1.45;
  display: flex;
  flex-direction: column;
  gap: 8px;

  button {
    align-self: flex-start;
    min-height: 40px;
    padding: 0 14px;
    border-radius: 9px;
    border: 1px solid currentColor;
    background: transparent;
    color: inherit;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
  }
`;

// ---------- Câmera embutida (getUserMedia) ----------
//
// Overlay em tela cheia. Paleta escura fixa (não segue o tema do app): é uma
// tela de câmera, o contraste tem que funcionar no sol do galpão. Respeita as
// safe areas para não esconder botão atrás do notch/barra de gestos.

export const CamOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9000;
  background: #000;
  display: flex;
  flex-direction: column;
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
`;

export const CamFecharBtn = styled.button`
  position: absolute;
  top: calc(env(safe-area-inset-top, 0px) + 10px);
  right: 12px;
  z-index: 1;
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border: none;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  cursor: pointer;
`;

export const CamPalco = styled.div`
  flex: 1;
  min-height: 0;
  display: grid;
  place-items: center;
  overflow: hidden;
`;

export const CamVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
`;

export const CamAcoes = styled.div`
  flex: none;
  min-height: 108px;
  padding: 14px 20px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

// 72px: alvo de toque generoso — quem usa está de luva, em pé, com pressa.
export const CamCapturarBtn = styled.button`
  width: 72px;
  height: 72px;
  margin: 0 auto;
  display: grid;
  place-items: center;
  border: 4px solid rgba(255, 255, 255, 0.35);
  border-radius: 50%;
  background: #fff;
  color: #16181e;
  cursor: pointer;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

export const CamTrocarBtn = styled.button`
  width: 48px;
  height: 48px;
  flex: none;
  display: grid;
  place-items: center;
  border: none;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  cursor: pointer;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

// Atalho discreto para a câmera do sistema (flash, foco por toque, HDR) —
// recursos que a câmera embutida não tem.
export const LinkCameraSistema = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: var(--accent);
  font: inherit;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
`;

// ---------- Rodapé sticky (zona do polegar) ----------

export const RodapeFixo = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 20;
  margin: 4px -16px -20px;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  background: var(--bg);
  border-top: 1px solid var(--border);

  @media (min-width: ${BP_DESKTOP}px) {
    margin: 4px 0 0;
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 12px 16px;
  }
`;

// ---------- Rascunho ----------

export const BannerRascunho = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 14px;
  border-radius: 10px;
  background: var(--info-bg);
  border: 1px solid var(--info-border);
  color: var(--info-text);
  font-size: 13.5px;

  span { flex: 1; min-width: 140px; }

  button {
    min-height: 40px;
    padding: 0 12px;
    border-radius: 8px;
    border: 1px solid var(--info-border);
    background: transparent;
    color: var(--info-text);
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
  }
`;

// ---------- Comprovante (retrato, largura 1080px) ----------
//
// O card é renderizado FORA da tela (540px de largura) e capturado pelo
// html2canvas com scale=2 → JPG de 1080px de largura; a ALTURA é 100% ditada
// pelo conteúdo (sem min-height): 1 peça gera um comprovante curto, 20 peças
// geram um comprido — sem faixa branca sobrando embaixo. Paleta FIXA clara
// (independe do dark mode do app): comprovante é documento compartilhável,
// precisa ficar legível em qualquer aparelho.
//
// height:0 + overflow:hidden: o card cresce a milhares de px; sem isso, o
// wrapper absoluto empurraria o scrollHeight do documento e criaria scroll
// morto na tela de sucesso. O filho ainda faz layout no tamanho intrínseco
// (o clip do pai só recorta pintura/scroll), então a captura continua íntegra.
export const CompOffscreen = styled.div`
  position: absolute;
  left: -10000px;
  top: 0;
  height: 0;
  overflow: hidden;
`;

export const ComprovanteCard = styled.div`
  width: 540px;
  box-sizing: border-box;
  background: #ffffff;
  color: #16181e;
  padding: 30px 28px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  font-family: inherit;
`;

export const CompRodape = styled.div`
  /* sem margin-top:auto — o card não tem mais altura mínima, então empurrar
     o rodapé para baixo só recriaria o vão branco que queremos eliminar. */
  border-top: 1px solid #e6e8ef;
  padding-top: 14px;
  font-size: 13px;
  color: #6b7280;
  display: flex;
  justify-content: space-between;
  gap: 10px;
`;

export const CompPecas = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const CompPecaBloco = styled.div`
  border: 1px solid #e6e8ef;
  border-left: 4px solid #5b5bf6;
  border-radius: 9px;
  padding: 8px 11px;
  display: flex;
  flex-direction: column;
  gap: 2px;

  .titulo-linha {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
  }
  /* cor + descrição na MESMA linha (cor na frente), quebrando se não couber. */
  .peca-txt { min-width: 0; font-size: 15px; overflow-wrap: anywhere; }
  .cor { color: #6b7280; font-weight: 400; }
  .desc { font-weight: 700; }
  /* quantidade na FRENTE da descrição ("2 Prateleira - Branco TX 18 mm").
     Tinta escura (não o roxo da marca): o comprovante é impresso 2-4 por folha
     A4 e em preto-e-branco — cor clara viraria cinza fraco nesse tamanho. */
  .qtd { font-weight: 800; color: #16181e; margin-right: 6px; }
  /* sem tabular-nums: no html2canvas a feature tnum diverge (largura medida vs.
     desenhada) e contribui p/ números com espaço fantasma ("11 00x65x1 8");
     a fonte normal tende a sair limpa ("1100x65x18"). */
  .medida {
    flex: none;
    font-size: 14.5px;
    font-weight: 700;
    color: #16181e;
    white-space: nowrap;
  }
  .subcor { font-size: 13px; color: #6b7280; }
`;

// Motivo da solicitação — fecha o comprovante, logo acima do rodapé.
export const CompMotivo = styled.div`
  border-top: 1px solid #e6e8ef;
  padding-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 3px;

  .rotulo { color: #6b7280; font-size: 12.5px; }
  .valor {
    font-size: 14.5px;
    font-weight: 600;
    color: #16181e;
    overflow-wrap: anywhere;
  }
`;

export const CompMarca = styled.div`
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #5b5bf6;
`;

export const CompNumero = styled.div`
  font-size: 42px;
  font-weight: 800;
  color: #16181e;
  line-height: 1.1;

  /* O número da solicitação é INDIVISÍVEL: sem este nowrap o algoritmo guloso
     de quebra escolhe o hífen ("ASM-UN1-" numa linha, "824" na outra), porque
     ele é a última oportunidade que cabe. Medido na fonte real do app (Sora,
     ~7% mais larga que a fallback Segoe UI): com o formato de lote usado hoje
     ("376-05") a linha dá 504px contra 484px úteis, então ELA QUEBRA — e tem
     que quebrar entre o chip e o número, nunca dentro do número. */
  .num { white-space: nowrap; }

  /* "LOTE 376-05" na frente do número, quando o comprovante sai pela impressão
     do lote de MDF. Menor que o número; e como o campo "número do lote" é
     texto livre de até 40 chars digitado pelo gestor, o chip pode quebrar por
     dentro em vez de vazar os 540px do card e ser cortado no html2canvas. */
  .lote {
    display: inline-block;
    max-width: 100%;
    font-size: 22px;
    font-weight: 800;
    line-height: 1.1;
    color: #16181e;
    background: #eef0fb;
    border: 1px solid #d4d8ee;
    border-radius: 8px;
    padding: 2px 9px;
    margin-right: 10px;
    vertical-align: 6px;
    overflow-wrap: anywhere;
  }
`;

export const CompLinhas = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 22px;
  row-gap: 9px;
  color: #16181e;
  border-top: 1px solid #e6e8ef;
  padding-top: 12px;

  /* cada campo empilhado (rótulo pequeno em cima, valor embaixo), 2 por linha —
     ocupa menos altura que a lista de uma coluna. */
  div { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
  dt, .rotulo { color: #6b7280; font-size: 12.5px; }
  dd, .valor { margin: 0; font-size: 14.5px; overflow-wrap: anywhere; font-weight: 600; }
`;

export const PrevisualizacaoImg = styled.img`
  width: 100%;
  max-width: 300px;
  /* comprovante longo (muitas peças) vira imagem alta; limita a prévia na tela
     p/ não virar uma tira gigante — não afeta o JPG gerado. */
  max-height: 70vh;
  object-fit: contain;
  border-radius: 12px;
  border: 1px solid var(--border);
  display: block;
  margin: 0 auto;
`;

export const AcoesColuna = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

// ---------- Detalhe ----------

export const BlocoDados = styled.dl`
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 14px;

  div { display: flex; justify-content: space-between; gap: 12px; }
  dt { color: var(--ink-2); flex: none; }
  dd { margin: 0; text-align: right; overflow-wrap: anywhere; font-weight: 500; }
`;

export const TimelineWrap = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

export const TimelineItem = styled.li`
  position: relative;
  padding: 0 0 16px 24px;

  &::before {
    content: "";
    position: absolute;
    left: 5px;
    top: 16px;
    bottom: 0;
    width: 2px;
    background: var(--border);
  }
  &:last-child::before { display: none; }
`;

export const TimelinePonto = styled.span`
  position: absolute;
  left: 0;
  top: 4px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  ${({ $tone }) => {
    const t = getToneTokens(null, $tone || "neutral");
    return `background:${t.accent}; box-shadow: 0 0 0 3px ${t.background};`;
  }}
`;

export const TimelineTexto = styled.div`
  font-size: 13.5px;
  color: var(--ink);

  strong { display: block; font-size: 14px; }
  small { color: var(--ink-2); display: block; margin-top: 2px; font-size: 12.5px; }
`;

// ---------- Combobox ----------

export const ComboWrap = styled.div`
  position: relative;
`;

export const ComboDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  z-index: 40;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
  max-height: 280px;
  overflow-y: auto;
`;

export const ComboItem = styled.button`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  min-height: 48px;
  justify-content: center;
  text-align: left;
  padding: 8px 14px;
  border: none;
  background: none;
  cursor: pointer;
  color: var(--ink);
  font-size: 14.5px;
  &:active, &:hover { background: var(--accent-soft); }
  small { color: var(--ink-2); font-size: 12px; }
`;

export const ComboVazio = styled.div`
  padding: 14px;
  font-size: 13px;
  color: var(--ink-2);
  text-align: center;
`;

export const ComboLimparBtn = styled.button`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border: none;
  background: none;
  color: var(--ink-2);
  cursor: pointer;
`;

// ==========================================================================
// Wizard — uma pergunta por vez (mobile first)
//
// Fluxo guiado: cada passo mostra UMA pergunta; obrigatórios ganham asterisco
// vermelho e travam o "Próxima" até serem preenchidos. Coluna estreita
// centrada no desktop; rodapé sticky na zona do polegar no celular.
// ==========================================================================

// Slide vertical (não horizontal) — respeita a regra "zero scroll lateral".
const passoEntra = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: none; }
`;

// Coluna do wizard: estreita e centrada (foco numa pergunta por vez).
export const WizardCol = styled.div`
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const WizardTopo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
`;

export const WizardVoltarIcone = styled.button`
  flex: none;
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);
  color: var(--ink);
  cursor: pointer;
  &:active { background: var(--accent-soft); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

export const WizardTopoTitulo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  strong {
    font-size: 16px;
    font-weight: 700;
    color: var(--ink);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  span { font-size: 12px; color: var(--ink-2); font-weight: 600; }
`;

export const ProgressoTrack = styled.div`
  height: 6px;
  width: 100%;
  border-radius: 999px;
  background: var(--accent-soft);
  overflow: hidden;
`;

export const ProgressoFill = styled.div`
  height: 100%;
  border-radius: 999px;
  background: var(--accent);
  transition: width 0.28s cubic-bezier(0.22, 0.61, 0.36, 1);

  html[data-animacoes="off"] & { transition: none; }
`;

export const PassoLinha = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

export const StepGrupoChip = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  padding: 3px 10px;
  border-radius: 999px;
  background: var(--accent-soft);
  color: var(--accent);
`;

export const PassoContador = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-2);
`;

export const StepArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: ${passoEntra} 0.26s cubic-bezier(0.22, 0.61, 0.36, 1);

  @media (prefers-reduced-motion: reduce) { animation: none; }
  html[data-animacoes="off"] & { animation: none; }
`;

export const StepPergunta = styled.h2`
  margin: 0;
  font-size: 21px;
  font-weight: 700;
  line-height: 1.25;
  color: var(--ink);
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
`;

export const Asterisco = styled.span`
  color: var(--danger-text);
  font-weight: 800;
`;

export const TagOpcional = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-2);
  background: var(--surface-2, var(--bg));
  border: 1px solid var(--border);
  padding: 1px 8px;
  border-radius: 999px;
  text-transform: lowercase;
  letter-spacing: 0;
`;

export const StepDescricao = styled.p`
  margin: -2px 0 0;
  font-size: 13.5px;
  color: var(--ink-2);
`;

export const StepCorpo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 4px;
`;

// Saída de dentro de uma peça/item: "Remover esta peça". Existe para o clique
// acidental em "Adicionar outra peça" ser reversível — o passo novo nasce
// inválido e fica ANTES do boundary, então sem isto o usuário não alcançava a
// revisão nem o "Remover" que só existia lá. Discreto de propósito: é uma
// saída, não um caminho sugerido.
// Alvo de 44px e tom de perigo em repouso (mesma convenção do
// RevisaoPecaAcao $danger): é uma ação destrutiva num formulário de chão de
// fábrica, tocada de luva — não pode ser um texto fininho, nem parecer um
// link inofensivo. Respiro grande embaixo para não encostar no rodapé fixo,
// onde fica o "Voltar".
export const StepDescartar = styled.button`
  align-self: flex-start;
  min-height: 44px;
  margin: 22px 0 8px;
  padding: 0 12px 0 0;
  border: none;
  background: none;
  color: var(--danger-text);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
`;

// Rodapé sticky com [Voltar] [Próxima/Enviar] — mesma convenção do RodapeFixo.
export const WizardRodape = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 20;
  margin: 8px -16px -20px;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  background: var(--bg);
  border-top: 1px solid var(--border);
  display: flex;
  gap: 10px;

  @media (min-width: ${BP_DESKTOP}px) {
    margin: 8px 0 0;
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 12px;
  }
`;

export const BtnSecundario = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 52px;
  padding: 0 18px;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink-2);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  &:active { background: var(--accent-soft); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export const BtnPrimario = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 52px;
  padding: 0 18px;
  border-radius: 12px;
  border: 1px solid var(--accent);
  background: var(--accent);
  color: var(--ink-on-accent);
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:not(:disabled):active { filter: brightness(0.95); }
`;

export const BtnFantasma = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 52px;
  border-radius: 12px;
  background: transparent;
  color: var(--accent);
  border: 1px dashed var(--accent);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  &:active { background: var(--accent-soft); }
`;

// ---------- Passo de fechamento de peça (boundary) ----------

export const BoundaryCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 24px 16px 8px;
  text-align: center;
`;

export const BoundaryIcone = styled.div`
  width: 60px;
  height: 60px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: var(--success-bg);
  color: var(--success-text);
  border: 1px solid var(--success-border);
`;

export const BoundaryTitulo = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: var(--ink);
`;

export const BoundaryBotoes = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  margin-top: 4px;
`;

// ---------- Revisão ----------

export const RevisaoLista = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

/**
 * Palco do desenho da peca na REVISAO (2D/3D). O seletor fica sobreposto no
 * canto, como na Gestao, para nao roubar altura da tela do celular.
 */
export const PrevisaoPalco = styled.div`
  position: relative;
  margin: 10px 0 4px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface-2, var(--surface));
  overflow: hidden;

  .carregando {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    color: var(--ink-2);
  }
`;

export const PrevisaoSeletor = styled.div`
  position: absolute;
  top: 6px;
  left: 6px;
  z-index: 2;
  display: inline-flex;
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
  background: var(--surface);

  button {
    border: none;
    background: transparent;
    color: var(--ink-2);
    font: inherit;
    font-size: 11px;
    font-weight: 700;
    /* 30px de alvo: dedo de luva, mesma regra do resto do wizard. */
    min-height: 30px;
    padding: 0 11px;
    cursor: pointer;
  }
  button[data-on="true"] {
    background: var(--accent-soft);
    color: var(--accent);
  }
`;

export const RevisaoGrupo = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const RevisaoGrupoTopo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 2px;
`;

export const RevisaoTitulo = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: var(--ink);
`;

export const RevisaoAcoes = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

export const RevisaoPecaAcao = styled.button`
  min-height: 44px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: ${({ $danger }) => ($danger ? "var(--danger-text)" : "var(--ink-2)")};
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  &:active { background: var(--accent-soft); }
`;

export const RevisaoLinha = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  font-size: 14px;

  .rot { color: var(--ink-2); flex: none; min-width: 84px; }
  .val {
    color: var(--ink);
    font-weight: 600;
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
  }
`;

export const RevisaoEditar = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* Alvo de toque >= 44px sem engordar a linha (margin negativa compensa). */
  min-height: 44px;
  margin: -8px 0;
  padding: 4px 10px;
  border: none;
  background: none;
  color: var(--accent);
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
`;
